<script setup lang="ts">
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import draggable from "vuedraggable"
import FormBuilderCanvasNode from "./FormBuilderCanvasNode.vue"
import { formElements } from "./formElements"
import type { EditorNode } from "./types"
import type { FormBuilder } from "./useFormBuilder"

const { node, formBuilder } = defineProps<{ node: EditorNode; formBuilder: FormBuilder }>()

const isSelected = computed(() => formBuilder.selectedId.value === node._id)

const { t } = useI18n()

const typeLabel = computed(() => t(formElements.get(node.type)!.label))!
const displayLabel = computed(() => node.label || node.name || "—")
</script>

<template>
    <div
        :class="['fb-node', { 'fb-node--selected': isSelected, 'fb-node--layout': node.type === 'layout' }]"
        @click.stop="formBuilder.selectNode(node._id)"
    >
        <div class="fb-node__header">
            <span class="fb-node__badge">{{ typeLabel }}</span>
            <span class="fb-node__display-label">{{ displayLabel }}</span>

            <div class="fb-node__actions">
                <button
                    type="button"
                    class="fb-node__btn"
                    :title="$t('crud:button.clone')"
                    @click.stop="formBuilder.duplicateNode(node._id)"
                >
                    <i class="fa fa-fw fa-copy"></i>
                </button>
                <button
                    type="button"
                    class="fb-node__btn fb-node__btn--danger"
                    :title="$t('crud:button.delete')"
                    @click.stop="formBuilder.deleteNode(node._id)"
                >
                    <i class="fa fa-fw fa-trash"></i>
                </button>
            </div>
        </div>

        <template v-if="node.type === 'layout'">
            <div class="fb-node__cols-info">{{ node.columns }} {{ $t("components:form_builder.columns") }}</div>
            <draggable :list="node.children" group="form-fields" item-key="_id" class="fb-node__children">
                <template #item="{ element }">
                    <FormBuilderCanvasNode :node="element" :formBuilder="formBuilder" />
                </template>
                <template #footer>
                    <div v-if="node.children.length === 0" class="fb-node__drop-hint">
                        <i class="fa fa-arrow-down"></i>
                        {{ $t("components:form_builder.drop_hint") }}
                    </div>
                </template>
            </draggable>
        </template>
    </div>
</template>

<style lang="scss" scoped>
.fb-node {
    border: 2px solid #dee2e6;
    border-radius: 6px;
    background: #fff;
    margin-bottom: 0.5rem;
    cursor: pointer;
    transition: border-color 0.12s, box-shadow 0.12s;

    &:hover:not(&--selected) {
        border-color: #adb5bd;
    }

    &--selected {
        border-color: #0d6efd;
        box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.15);
    }

    &--layout {
        background: #f8f9fa;
    }

    &:hover, &--selected {
        .fb-node__actions {
            opacity: 1;
        }
    }

    &__header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.45rem 0.65rem;
        min-height: 36px;
    }

    &__badge {
        font-size: 0.65rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        padding: 0.15rem 0.4rem;
        border-radius: 3px;
        background: #e9ecef;
        color: #495057;
        white-space: nowrap;
        flex-shrink: 0;
    }

    &__display-label {
        font-size: 0.8rem;
        color: #495057;
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__actions {
        display: flex;
        gap: 0.15rem;
        margin-left: auto;
        flex-shrink: 0;
        opacity: 0;
        transition: opacity 0.12s;
    }

    &__btn {
        padding: 0.2rem 0.35rem;
        line-height: 1;
        background: transparent;
        border: 1px solid transparent;
        border-radius: 4px;
        color: #6c757d;
        cursor: pointer;
        transition: background 0.1s, color 0.1s;

        &:hover {
            background: #e9ecef;
            color: #212529;
        }

        &--danger:hover {
            background: #f8d7da;
            color: #dc3545;
        }
    }

    &__cols-info {
        font-size: 0.7rem;
        color: #adb5bd;
        padding: 0 0.65rem 0.3rem;
    }

    &__children {
        min-height: 52px;
        padding: 0.5rem;
        border-top: 1px solid #dee2e6;
        border-radius: 0 0 4px 4px;
    }

    &__drop-hint {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.4rem;
        min-height: 40px;
        color: #ced4da;
        font-size: 0.75rem;
    }
}
</style>
